import { AnalyticsChartCard } from '@/components/ui/analytics/AnalyticsChartCard';
import { SummaryMetricCard } from '@/components/ui/analytics/SummaryMetricCard';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/shadcn/chart';
import { useChartHeightClass } from '@/hooks/use-chart-height';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import { categoricalColor } from '@/lib/chart-colors';
import { formatCurrency } from '@/lib/utils';
import type { EmployeePerformance, TeamAnalyticsData } from '@/types/analytics';
import { BriefcaseBusiness, Users2 } from 'lucide-react';
import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, XAxis, YAxis } from 'recharts';

interface Props {
    employees: EmployeePerformance[];
    teamAnalytics?: TeamAnalyticsData;
}

export default function TeamAnalytics({ employees, teamAnalytics }: Props) {
    const { t } = useTranslations();
    const isMobile = useIsMobile();
    const categoryAxisWidth = isMobile ? 72 : 120;
    const truncateCategoryTick = (v: string) => (isMobile && v.length > 10 ? `${v.slice(0, 9)}…` : v);
    const chartHeightClass = useChartHeightClass('md');
    const activityHeightClass = isMobile ? 'h-[220px]' : 'h-[280px]';

    const kpis = useMemo(() => {
        const teamSize = Number(teamAnalytics?.team_size ?? employees.length);
        const salesOwned = employees.reduce((sum, e) => sum + Number(e.total_revenue ?? 0), 0);
        return { teamSize, salesOwned };
    }, [employees, teamAnalytics]);

    const salesTab = teamAnalytics?.sales_tab ?? [];
    const projectsTab = teamAnalytics?.projects_tab ?? [];

    // Activity tab: productivity trend (login / task activity)
    const activityTab = teamAnalytics?.productivity_trend?.length ? teamAnalytics.productivity_trend : [];

    const hasActivityData = activityTab.length > 0 && activityTab.some((r) => (r.completedTasks ?? 0) > 0 || (r.logins ?? 0) > 0);

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-semibold">{t('analytics.labels.page_8_employees_dashboard', 'Employees Dashboard')}</h2>
                <p className="text-muted-foreground text-sm">
                    {t('analytics.labels.sales_project_service_activity', 'Sales, project and activity performance by employee.')}
                </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-2">
                <SummaryMetricCard
                    docKey="team.team_size"
                    title={t('analytics.labels.team_size', 'Team Size')}
                    value={kpis.teamSize}
                    subtitle={t('analytics.labels.active_employees', 'Active (non-suspended) employees')}
                    icon={<Users2 className="h-4 w-4" />}
                />
                <SummaryMetricCard
                    docKey="team.sales_revenue_owned"
                    title={t('analytics.labels.sales_revenue_owned', 'Sales Revenue Owned')}
                    value={formatCurrency(kpis.salesOwned)}
                    subtitle={t('analytics.labels.owned_commercial_output', 'Owned commercial output')}
                    icon={<BriefcaseBusiness className="h-4 w-4" />}
                />
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                {/* Sales Tab */}
                <AnalyticsChartCard docKey="team.sales_tab" title={t('analytics.labels.sales_tab', 'Sales Tab')}>
                    {salesTab.length > 0 ? (
                        <ChartContainer
                            role="img"
                            aria-label={t('analytics.labels.sales_tab', 'Sales Tab')}
                            config={{ value: { label: t('analytics.labels.revenue', 'Revenue'), color: '#2563eb' } }}
                            className={chartHeightClass}
                        >
                            <BarChart data={salesTab} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted opacity-30" />
                                <XAxis type="number" tickFormatter={(v) => formatCurrency(Number(v))} tick={{ fontSize: 10 }} />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    width={categoryAxisWidth}
                                    tickFormatter={truncateCategoryTick}
                                    tick={{ fontSize: 11 }}
                                />
                                <ChartTooltip content={<ChartTooltipContent formatter={(v) => formatCurrency(Number(v))} />} />
                                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                    {salesTab.map((_, i) => (
                                        <Cell key={i} fill={categoricalColor(i)} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ChartContainer>
                    ) : (
                        <div className={`text-muted-foreground flex ${chartHeightClass} items-center justify-center text-sm`}>
                            {t('analytics.labels.no_revenue_in_period', 'No revenue in this period')}
                        </div>
                    )}
                </AnalyticsChartCard>

                {/* Projects Tab */}
                <AnalyticsChartCard docKey="team.projects_tab" title={t('analytics.labels.projects_tab', 'Projects Tab')}>
                    {projectsTab.length > 0 ? (
                        <ChartContainer
                            role="img"
                            aria-label={t('analytics.labels.projects_tab', 'Projects Tab')}
                            config={{ value: { label: t('analytics.labels.projects', 'Projects'), color: '#7c3aed' } }}
                            className={chartHeightClass}
                        >
                            <BarChart data={projectsTab} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted opacity-30" />
                                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    width={categoryAxisWidth}
                                    tickFormatter={truncateCategoryTick}
                                    tick={{ fontSize: 11 }}
                                />
                                <ChartTooltip content={<ChartTooltipContent />} />
                                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                    {projectsTab.map((_, i) => (
                                        <Cell key={i} fill={categoricalColor(i)} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ChartContainer>
                    ) : (
                        <div className={`text-muted-foreground flex ${chartHeightClass} items-center justify-center text-sm`}>
                            {t('analytics.labels.no_projects_in_period', 'No projects in this period')}
                        </div>
                    )}
                </AnalyticsChartCard>
            </div>

            {/* Activity Tab */}
            <AnalyticsChartCard docKey="team.activity_tab" title={t('analytics.labels.activity_tab', 'Activity Tab')}>
                {hasActivityData ? (
                    <ChartContainer
                        role="img"
                        aria-label={t('analytics.labels.activity_tab', 'Activity Tab')}
                        config={{ completedTasks: { label: t('analytics.labels.tasks', 'Tasks'), color: '#ea580c' } }}
                        className={activityHeightClass}
                    >
                        <LineChart data={activityTab}>
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted opacity-30" />
                            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                            <ChartTooltip content={<ChartTooltipContent />} />
                            <Line dataKey="completedTasks" stroke="#ea580c" strokeWidth={2} dot={false} />
                        </LineChart>
                    </ChartContainer>
                ) : (
                    <div className="text-muted-foreground flex h-[120px] flex-col items-center justify-center gap-1 text-sm">
                        <span>{t('analytics.labels.no_activity_data', 'No activity trend data available for this period.')}</span>
                    </div>
                )}
            </AnalyticsChartCard>
        </div>
    );
}
